"use client";

import { useEffect, useRef } from "react";
import { draw } from "@chordbook/charts";
import {
  type Instrument,
  getChordsDbPosition,
  convertToChordboxFormat,
  INSTRUMENT_STRINGS,
} from "@/lib/utils/chords-db";

interface ChordDiagramProps {
  chordName: string;
  instrument?: Instrument;
  width?: number;
  height?: number;
  className?: string;
}

export function ChordDiagram({
  chordName,
  instrument = "guitar",
  width = 60,
  height = 72,
  className = "",
}: ChordDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.innerHTML = "";

    const position = getChordsDbPosition(chordName, instrument);
    if (!position) return;

    const numStrings = INSTRUMENT_STRINGS[instrument];
    const chordboxData = convertToChordboxFormat(position, numStrings);

    draw(containerRef.current, chordboxData, {
      width,
      height,
      showTuning: false,
      defaultColor: "currentColor",
      bgColor: "transparent",
      strokeColor: "var(--primary)",
      numStrings,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }, [chordName, instrument, width, height]);

  const position = getChordsDbPosition(chordName, instrument);
  if (!position) return null;

  return (
    <div className={`flex flex-col items-center p-1 ${className}`}>
      <div ref={containerRef} className="inline-block" title={chordName} />
      <span className="text-sm font-semibold text-center">{chordName}</span>
    </div>
  );
}

// Get chord data for external use
export function getChordData(chordName: string, instrument: Instrument = "guitar") {
  return getChordsDbPosition(chordName, instrument);
}

// List of available chords (not used externally, kept for compat)
export function getAvailableChords(): string[] {
  return [];
}
